'use client'

import type { ReactElement } from 'react'
import type { Swiper as SwiperType } from 'swiper'

import { useEffect, useRef } from 'react'
import { Autoplay } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import 'swiper/css'
import './slideCarousel.css'

/**
 * Single-slide carousel — the Swiper replacement for `react-responsive-carousel`.
 *
 * Deliberately shaped like the RRC API it replaces (`index`/`onIndexChange` for
 * RRC's `selectedItem`/`onChange`) so the "synced pair" pattern used by the home
 * Promotions and MY alrajhi Deals sections migrates as a straight swap: render
 * two of these sharing one `useState` index and they stay in lockstep.
 *
 * Omit `index` for an uncontrolled carousel (the article-sidebar promo rail).
 *
 * On a synced pair, set `autoPlay` on ONE instance only. RRC had both halves
 * autoplaying and relied on them landing on the same index; driving the pair
 * from a single timer removes that race without changing what a user sees.
 */
export interface SlideCarouselProps {
  children: ReactElement[]
  /** Controlled slide index. Omit for uncontrolled. */
  index?: number
  onIndexChange?: (index: number) => void
  /** RRC's `infiniteLoop`. */
  loop?: boolean
  /** RRC's `autoPlay`. On a synced pair, enable on one instance only. */
  autoPlay?: boolean
  /** RRC's `interval`, ms. */
  interval?: number
  speed?: number
  className?: string
}

export function SlideCarousel({
  children,
  index,
  onIndexChange,
  loop = false,
  autoPlay = false,
  interval = 3000,
  speed = 350,
  className = ''
}: SlideCarouselProps) {
  const swiperRef = useRef<SwiperType | null>(null)

  // Drive Swiper from the external index. The equality guard stops the loop
  // that would otherwise form with `onSlideChange` → `onIndexChange` → here.
  useEffect(() => {
    const swiper = swiperRef.current
    if (!swiper || swiper.destroyed || index == null) return
    const current = loop ? swiper.realIndex : swiper.activeIndex
    if (current === index) return
    if (loop) swiper.slideToLoop(index, speed)
    else swiper.slideTo(index, speed)
  }, [index, loop, speed])

  return (
    <Swiper
      className={`slide-carousel ${className}`.trim()}
      modules={autoPlay ? [Autoplay] : []}
      slidesPerView={1}
      speed={speed}
      loop={loop}
      // `disableOnInteraction: false` keeps RRC's behaviour of resuming the
      // timer after a swipe; RRC's `stopOnHover={false}` is Swiper's default.
      autoplay={autoPlay ? { delay: interval, disableOnInteraction: false } : undefined}
      onSwiper={swiper => (swiperRef.current = swiper)}
      onSlideChange={swiper => onIndexChange?.(loop ? swiper.realIndex : swiper.activeIndex)}
    >
      {children.map((child, i) => (
        <SwiperSlide key={child.key ?? i}>{child}</SwiperSlide>
      ))}
    </Swiper>
  )
}

export default SlideCarousel
